import { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOLD = "#C9A96E";
const LINK = /(https?:\/\/[^\s,]+[^\s,.]|www\.[^\s,]+[^\s,.]|\/(?:confidentialite|mentions-legales)\b)/g;
const Linkify = ({ t }) => t.split(LINK).map((x, i) => (i % 2
  ? <a key={i} href={x.startsWith("/") || x.startsWith("http") ? x : `https://${x}`} className="underline underline-offset-2 hover:opacity-80" target={x.startsWith("/") ? undefined : "_blank"} rel="noreferrer">{x}</a>
  : x));

const Block = ({ lines }) => (lines.every((l) => l.startsWith("- "))
  ? <ul className="mt-4 space-y-2.5">{lines.map((l, j) => <li key={j} className="flex gap-3 leading-relaxed"><span className="mt-2.5 h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: GOLD }} /><span><Linkify t={l.slice(2)} /></span></li>)}</ul>
  : <p className="mt-4 leading-relaxed"><Linkify t={lines.join(" ")} /></p>);

const parse = (text) => {
  const out = [];
  for (const raw of text.replace(/\r/g, "").split(/\n\s*\n/)) {
    const lines = raw.trim().split("\n").filter((l) => l.trim());
    if (!lines.length) continue;
    if (lines[0].startsWith("## ")) out.push({ title: lines.shift().slice(3), blocks: [] });
    else if (!out.length) out.push({ title: "", blocks: [] });
    if (lines.length) out[out.length - 1].blocks.push(lines);
  }
  return out;
};

export const PrivacyBody = ({ text }) => parse(text).map((s, i) => (
  <section key={i} className="pt-10 first:pt-0">
    {s.title && <><h2 className="pv-serif text-2xl sm:text-3xl tracking-tight" style={{ color: "var(--pv-head)" }} data-testid={`privacy-h-${i}`}>{s.title}</h2><div className="mt-3 h-px w-16" style={{ backgroundColor: GOLD }} /></>}
    {s.blocks.map((b, j) => <Block key={j} lines={b} />)}
  </section>
));

const lum = (hex) => { const n = parseInt((hex || "#000000").slice(1), 16); return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255; };
const headColor = (bg, fg) => (lum(bg) < 0.5 ? (lum(fg) > 0.5 ? "#FFFFFF" : fg) : (lum(fg) < 0.5 ? "#0B1B33" : fg));

export const ProsePage = ({ endpoint, title, subtitle, prefix }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}${endpoint}`).then((r) => setD(r.data)).catch(() => setD({ text: "", bg: "#0B1B33", fg: "#D1D5DB", company_name: "" }));
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600&family=Manrope:wght@400;500&display=swap";
    document.head.appendChild(l);
    document.title = title;
    return () => l.remove();
  }, [endpoint, title]);
  if (!d) return <div className="min-h-screen" style={{ backgroundColor: "#0B1B33" }} />;
  return (
    <div data-testid={`${prefix}-page`} className="min-h-screen" style={{ backgroundColor: d.bg, color: d.fg, fontFamily: "Manrope, sans-serif", "--pv-head": headColor(d.bg, d.fg) }}>
      <style>{".pv-serif{font-family:'Cormorant Garamond',serif;font-weight:600}"}</style>
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em]" style={{ color: "#C9A96E" }}><ShieldCheck className="h-4 w-4" />{d.company_name}</div>
        <h1 data-testid={`${prefix}-title`} className="pv-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-tight" style={{ color: "var(--pv-head)" }}>{title}</h1>
        <p className="mt-4 text-sm opacity-70">{subtitle}{d.updated_at && ` · mise à jour le ${new Date(d.updated_at).toLocaleDateString("fr-FR")}`}</p>
        <div className="mt-14 text-[15px]" data-testid={`${prefix}-body`}><PrivacyBody text={d.text} /></div>
        <footer className="mt-20 pt-6 border-t text-xs flex flex-wrap gap-x-4 gap-y-1" style={{ borderColor: "rgba(127,127,127,0.25)" }} data-testid={`${prefix}-footer`}>
          <span className="opacity-60">© 2026 SOLUTIONS PRIME — Tous droits réservés</span>
          <a href="/confidentialite" className="underline opacity-80 hover:opacity-100" data-testid={`${prefix}-link-privacy`}>Politique de confidentialité</a>
          <a href="/mentions-legales" className="underline opacity-80 hover:opacity-100" data-testid={`${prefix}-link-legal`}>Mentions légales</a>
        </footer>
      </div>
    </div>
  );
};

export default function Privacy() {
  return <ProsePage endpoint="/public/privacy" title="Politique de confidentialité" subtitle="Protection des données personnelles — Règlement (UE) 2016/679 (RGPD)" prefix="privacy" />;
}
